<template>
  <div class="searchable-select" :class="{ 'multiple': multiple }">
    <div class="select-container" @click="toggleDropdown">
      <div class="select-input">
        <div v-if="multiple" class="selected-items">
          <span v-if="internalValue.length === 0" class="placeholder">{{ placeholder }}</span>
          <span v-else class="selected-item" v-for="item in displaySelectedItems" :key="getItemKey(item)">
            {{ getItemLabel(item) }}
            <button type="button" @click.stop="removeItem(item)" class="remove-btn">&times;</button>
          </span>
        </div>
        <input 
          v-else
          :value="currentDisplayText" 
          :placeholder="placeholder"
          readonly
          class="select-input-field"
        />
        <div class="select-arrow" :class="{ 'open': isOpen }">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="6,9 12,15 18,9"></polyline>
          </svg>
        </div>
      </div>
    </div>

    <div v-if="isOpen" class="dropdown">
      <div class="dropdown-header">
        <input 
          v-model="searchText"
          :placeholder="searchPlaceholder"
          class="dropdown-search"
          ref="dropdownSearch"
          @click.stop
        />
        <div v-if="multiple" class="multiple-controls">
          <button @click.stop="selectAll" type="button" class="control-btn">Minden kijelölése</button>
          <button @click.stop="clearAll" type="button" class="control-btn">Összes törlése</button>
        </div>
      </div>
      
      <div class="dropdown-options">
        <div 
          v-for="item in filteredOptions" 
          :key="getItemKey(item)"
          @click.stop="selectItem(item)"
          class="option"
          :class="{ 'selected': isSelected(item) }"
        >
          <span class="option-label">{{ getItemLabel(item) }}</span>
          <span v-if="multiple && isSelected(item)" class="checkmark">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="20,6 9,17 4,12"></polyline>
            </svg>
          </span>
        </div>
        
        <div v-if="filteredOptions.length === 0" class="no-results">
          <span v-if="searchText">Nincs találat a keresésre: "{{ searchText }}"</span>
          <span v-else>Nincs elérhető opció</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SearchableSelect',
  props: {
    modelValue: { type: [String, Number, Array], default: '' },
    options: { type: Array, default: () => [] },
    placeholder: { type: String, default: 'Válassz opciót...' },
    searchPlaceholder: { type: String, default: 'Keresés...' },
    multiple: { type: Boolean, default: false },
    labelKey: { type: String, default: 'name' },
    valueKey: { type: String, default: 'id' },
    maxDisplayItems: { type: Number, default: 3 }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      isOpen: false,
      searchText: '',
      internalValue: this.multiple ? [] : ''
    };
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newVal) {
        this.internalValue = newVal;
      }
    },
    internalValue(newVal) {
      this.$emit('update:modelValue', newVal);
    }
  },
  computed: {
    currentDisplayText() {
      if (this.multiple || !this.internalValue) return '';
      
      const selectedItem = this.options.find(item => 
        this.getItemKey(item) === this.internalValue
      );
      return selectedItem ? this.getItemLabel(selectedItem) : '';
    },
    
    selectedItems() {
      if (this.multiple) {
        if (!Array.isArray(this.internalValue)) return [];
        return this.options.filter(item => 
          this.internalValue.includes(this.getItemKey(item))
        );
      } else {
        if (!this.internalValue) return [];
        return this.options.filter(item => 
          this.getItemKey(item) === this.internalValue
        );
      }
    },
    
    displaySelectedItems() {
      if (!this.multiple) return [];
      if (this.selectedItems.length <= this.maxDisplayItems) {
        return this.selectedItems;
      }
      return this.selectedItems.slice(0, this.maxDisplayItems);
    },
    
    filteredOptions() {
      if (!this.searchText) {
        return this.options;
      }
      const searchLower = this.searchText.toLowerCase();
      return this.options.filter(item => 
        this.getItemLabel(item).toLowerCase().includes(searchLower)
      );
    }
  },
  methods: {
    getItemLabel(item) {
      try {
        let obj = item;
        // Handle Vue Proxy by accessing the target
        if (obj && typeof obj === 'object') {
          // Try to stringify and parse to unwrap proxy
          try {
            obj = JSON.parse(JSON.stringify(obj));
          } catch (e) {
            // If that fails, use as-is
          }
        }
        
        if (typeof obj === 'object' && obj !== null) {
          const label = obj[this.labelKey];
          if (label !== undefined && label !== null) return String(label);
          if (obj.name) return String(obj.name);
          if (obj.def_name) return String(obj.def_name);
        }
        return String(obj);
      } catch (e) {
        return String(item);
      }
    },
    
    getItemKey(item) {
      try {
        let obj = item;
        // Handle Vue Proxy
        if (obj && typeof obj === 'object') {
          try {
            obj = JSON.parse(JSON.stringify(obj));
          } catch (e) {
            // If that fails, use as-is
          }
        }
        
        if (typeof obj === 'object' && obj !== null) {
          const value = obj[this.valueKey];
          if (value !== undefined && value !== null) return value;
          if (obj.id !== undefined && obj.id !== null) return obj.id;
        }
        return item;
      } catch (e) {
        return item;
      }
    },
    
    isSelected(item) {
      const itemKey = this.getItemKey(item);
      
      if (this.multiple) {
        return Array.isArray(this.internalValue) && 
               this.internalValue.includes(itemKey);
      } else {
        return this.internalValue === itemKey;
      }
    },
    
    selectItem(item) {
      const itemKey = this.getItemKey(item);
      
      if (this.multiple) {
        const currentValues = Array.isArray(this.internalValue) ? [...this.internalValue] : [];
        const index = currentValues.indexOf(itemKey);
        
        if (index > -1) {
          currentValues.splice(index, 1);
        } else {
          currentValues.push(itemKey);
        }
        this.internalValue = currentValues;
      } else {
        this.internalValue = itemKey;
        this.closeDropdown();
      }
      this.searchText = '';
    },
    
    removeItem(item) {
      if (!this.multiple) return;
      const itemKey = this.getItemKey(item);
      const currentValues = Array.isArray(this.internalValue) ? [...this.internalValue] : [];
      const index = currentValues.indexOf(itemKey);
      
      if (index > -1) {
        currentValues.splice(index, 1);
        this.internalValue = currentValues;
      }
    },
    
    selectAll() {
      if (!this.multiple) return;
      this.internalValue = this.options.map(item => this.getItemKey(item));
    },
    
    clearAll() {
      this.internalValue = this.multiple ? [] : '';
    },
    
    toggleDropdown() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.$nextTick(() => {
          if (this.$refs.dropdownSearch) {
            this.$refs.dropdownSearch.focus();
          }
        });
      } else {
        this.closeDropdown();
      }
    },
    
    closeDropdown() {
      this.isOpen = false;
      this.searchText = '';
    },
    
    handleOutsideClick(event) {
      if (this.$el && !this.$el.contains(event.target)) {
        this.closeDropdown();
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  }
};
</script>

<style scoped>
.searchable-select {
  position: relative;
  width: 100%;
}

.select-container {
  cursor: pointer;
}

.select-input {
  position: relative;
  min-height: 48px;
  border: 1px solid var(--color-border, #d1d5db);
  border-radius: 8px;
  background: var(--color-background, #ffffff);
  padding: 0.75rem;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.select-input:focus-within {
  outline: none;
  border-color: var(--primary-blue, #2563eb);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.select-input-field {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: var(--color-text, #1f2937);
  font-size: 0.95rem;
  padding: 0;
  min-width: 100px;
}

.select-input-field::placeholder {
  color: var(--color-text-muted, #9ca3af);
}

.selected-items {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  flex: 1;
  width: 100%;
}

.selected-item {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background: var(--primary-blue, #2563eb);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.85rem;
  white-space: nowrap;
}

.remove-btn {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0 0.25rem;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
  font-size: 1rem;
  line-height: 1;
}

.remove-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.placeholder {
  color: var(--color-text, #1f2937);
  font-size: 0.95rem;
  font-weight: 500;
  background-color: transparent;
}

.select-arrow {
  color: var(--color-text-muted, #9ca3af);
  transition: transform 0.2s;
  margin-left: auto;
  flex-shrink: 0;
}

.select-arrow.open svg {
  transform: rotate(180deg);
}

.dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--color-background, #ffffff);
  border: 1px solid var(--color-border, #d1d5db);
  border-radius: 8px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 300px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  margin-top: 4px;
}

.dropdown-header {
  padding: 0.75rem;
  border-bottom: 1px solid var(--color-border, #d1d5db);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.dropdown-search {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid var(--color-border, #d1d5db);
  border-radius: 6px;
  background: var(--color-background-mute, #f9fafb);
  color: var(--color-text, #1f2937);
  font-size: 0.9rem;
}

.dropdown-search::placeholder {
  color: var(--color-text-muted, #9ca3af);
}

.dropdown-search:focus {
  outline: none;
  border-color: var(--primary-blue, #2563eb);
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
}

.multiple-controls {
  display: flex;
  gap: 0.5rem;
}

.control-btn {
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--color-border, #d1d5db);
  background: var(--color-background, #ffffff);
  color: var(--color-text, #1f2937);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.2s;
}

.control-btn:hover {
  background: var(--color-background-mute, #f9fafb);
}

.dropdown-options {
  flex: 1;
  overflow-y: auto;
  background: var(--color-background, #ffffff);
}

.option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid var(--color-border, #eff2f5);
  color: var(--color-text, #1f2937);
}

.option:last-child {
  border-bottom: none;
}

.option:hover {
  background: var(--color-background-mute, #f9fafb);
}

.option.selected {
  background: rgba(37, 99, 235, 0.1);
  color: var(--primary-blue, #2563eb);
  font-weight: 500;
}

.option-label {
  flex: 1;
  color: inherit;
}

.checkmark {
  color: var(--primary-blue, #2563eb);
  margin-left: 0.5rem;
  flex-shrink: 0;
}

.no-results {
  padding: 2rem 1rem;
  text-align: center;
  color: var(--color-text-muted, #9ca3af);
  font-style: italic;
}

@media (max-width: 768px) {
  .multiple-controls {
    flex-direction: column;
  }
  
  .control-btn {
    font-size: 0.8rem;
    padding: 0.4rem 0.8rem;
  }
}
</style>